import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Search, 
    Filter, 
    Building2, 
    MoreVertical, 
    ExternalLink, 
    CheckCircle2, 
    XCircle, 
    AlertCircle, 
    Clock,
    MapPin,
    Users,
    GraduationCap,
    Loader2,
    ChevronLeft,
    ChevronRight,
    ArrowUpDown
} from 'lucide-react';
import { Link } from 'react-router-dom';

const StatusBadge = ({ status }) => {
    const configs = {
        active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
        trial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
        suspended: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
        expired: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
        cancelled: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
    };

    const config = configs[status] || configs.cancelled;
    const Icon = config.icon;

    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${config.bg} ${config.text}`}>
            <Icon className="h-3 w-3" />
            {status}
        </span>
    );
};

const InstitutionManagement = () => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    useEffect(() => {
        fetchInstitutions();
    }, [pagination.page, statusFilter, sortBy, sortOrder]);

    const fetchInstitutions = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/super-admin/institutions', {
                params: {
                    search,
                    status: statusFilter,
                    page: pagination.page,
                    sortBy,
                    sortOrder
                }
            });
            setInstitutions(response.data.institutions);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching institutions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPagination({ ...pagination, page: 1 });
        fetchInstitutions();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(field);
            setSortOrder('DESC');
        }
    };

    return (
        <div className="space-y-6 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Institutions</h1>
                    <p className="text-slate-500 text-sm mt-1">Manage all registered colleges and schools</p>
                </div>
                <div className="flex items-center gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all shadow-sm">
                        <ArrowUpDown className="h-4 w-4" /> Export Data
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
                <form onSubmit={handleSearchSubmit} className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input 
                        type="text" 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, city or email..." 
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                    />
                </form>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <Filter className="h-4 w-4 text-slate-400 mr-1" />
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 md:w-44 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                    >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="trial">Trial</option>
                        <option value="suspended">Suspended</option>
                        <option value="expired">Expired</option>
                    </select>
                </div>
                
                <button 
                    onClick={() => { setPagination({ ...pagination, page: 1 }); fetchInstitutions(); }}
                    className="w-full md:w-auto px-6 py-2 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-all shadow-md shadow-slate-200"
                >
                    Apply
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th onClick={() => toggleSort('name')} className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-600">
                                    <div className="flex items-center gap-2">Institution <ArrowUpDown className="h-3 w-3" /></div>
                                </th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Learning Mode</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">Stats</th>
                                <th onClick={() => toggleSort('createdAt')} className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-600 text-right">
                                    <div className="flex items-center gap-2 justify-end">Signed Up <ArrowUpDown className="h-3 w-3" /></div>
                                </th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                            <span className="text-sm font-medium text-slate-400">Loading institutions...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : institutions.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                                        No institutions found matching your criteria
                                    </td>
                                </tr>
                            ) : (
                                institutions.map((inst) => (
                                    <tr key={inst.id} className="hover:bg-slate-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-slate-500 border border-slate-50">
                                                    {inst.name[0]}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-bold text-slate-900 truncate group-hover:text-blue-600 transition-colors">{inst.name}</p>
                                                    <p className="text-xs text-slate-400 flex items-center gap-1">
                                                        <MapPin className="h-3 w-3" /> {inst.city}, {inst.country}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <StatusBadge status={inst.subscription?.status} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">
                                                {inst.learningMode === 'instructor_led' ? 'Instructor-Led' : 'Self-Paced'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center gap-4">
                                                <div className="text-center" title="Students">
                                                    <p className="text-xs font-bold text-slate-900">{inst.stats?.studentCount}</p>
                                                    <Users className="h-3 w-3 text-slate-300 mx-auto" />
                                                </div>
                                                <div className="text-center" title="Lecturers">
                                                    <p className="text-xs font-bold text-slate-900">{inst.stats?.lecturerCount}</p>
                                                    <GraduationCap className="h-3 w-3 text-slate-300 mx-auto" />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <p className="text-sm font-medium text-slate-900">{new Date(inst.createdAt).toLocaleDateString()}</p>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Joined Platform</p>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link 
                                                    to={`/super-admin/institutions/${inst.id}`}
                                                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                    title="View Details"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </Link>
                                                <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all">
                                                    <MoreVertical className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && institutions.length > 0 && (
                    <div className="px-6 py-4 border-t border-slate-50 flex items-center justify-between bg-slate-50/30">
                        <p className="text-xs font-medium text-slate-500">
                            Showing <span className="font-bold text-slate-900">{institutions.length}</span> of <span className="font-bold text-slate-900">{pagination.total}</span> institutions
                        </p>
                        <div className="flex items-center gap-2">
                            <button 
                                disabled={pagination.page === 1}
                                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                className="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-slate-900 disabled:opacity-50 transition-all shadow-sm"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-sm font-bold text-slate-700 px-2">Page {pagination.page} of {pagination.pages}</span>
                            <button 
                                disabled={pagination.page === pagination.pages}
                                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                className="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-slate-900 disabled:opacity-50 transition-all shadow-sm"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstitutionManagement;
